import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Database, BarChart3, ArrowRight, Mail, Megaphone, UserCheck, Calendar, Vote } from 'lucide-react';
import Button from '../components/Button';
import StatCard, { StatCardRow } from '../components/StatCard';
import { api } from '../App';
import { PeopleIcon } from '../components/icons';

const AdminPage: React.FC = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrganizations: 0,
    totalWorkspaces: 0,
    totalBills: 0
  });
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  /*
    This fetch loads the stat tiles. It is NOT an authorization check.

    It used to be treated as one: any failure — a 500, a timeout, a dropped
    connection — redirected to /dashboard, so a transient backend blip ejected a
    legitimate system admin out of the admin panel (issue #1047).

    Authorization is already enforced by `ProtectedRoute requireAdmin` on this
    route in App.tsx. That is the real gate; this was a second, weaker one that
    failed open into a redirect. So a failure here shows an inline error with a
    retry, and only a genuine 401/403 defers to the router by reloading — which
    lets ProtectedRoute make the decision rather than this component guessing.
  */
  const loadStats = React.useCallback(async () => {
    setLoading(true);
    setStatsError(null);
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Really not authorized — let the route guard handle it.
        window.location.href = '/dashboard';
        return;
      }
      console.error('Failed to load admin stats:', error);
      setStatsError(
        error?.response?.data?.error || 'Could not load admin statistics. The admin tools below still work.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold">Admin Panel</h1>
            <p className="text-text-tertiary dark:text-text-dark-tertiary">
              Manage organizations, users, and data imports for the platform
            </p>
          </div>
        </div>
      </div>

      {/* Stats failed but the admin is still an admin — say so and offer a retry
          rather than throwing them out of the panel (issue #1047). */}
      {statsError && (
        <div
          role="alert"
          className="flex items-start justify-between gap-4 rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 px-4 py-3"
        >
          <p className="text-sm text-warning-800 dark:text-warning-200">{statsError}</p>
          <Button variant="secondary" size="sm" onClick={loadStats}>
            Retry
          </Button>
        </div>
      )}

          {/* Quick Stats */}
          <StatCardRow>
            <StatCard label="Total Users" value={stats.totalUsers} icon={PeopleIcon} />
            <StatCard label="Organizations" value={stats.totalOrganizations} icon={Building2} />
            <StatCard label="Workspaces" value={stats.totalWorkspaces} icon={BarChart3} />
            <StatCard label="Bills" value={stats.totalBills} icon={Database} />
          </StatCardRow>

          {/* Admin Tools Navigation */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            
            {/* Accounts (admin-next) — replaced the legacy Organization, User,
                Workspace, and User Analytics tools */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <PeopleIcon className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Accounts</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Users, organizations, and workspaces — one connected view
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Manage users, organizations, and workspaces</li>
                    <li>• Deactivate/reactivate, memberships, invitations</li>
                    <li>• Bulk operations and user simulation</li>
                    <li>• Login activity and admin audit log</li>
                  </ul>
                </div>
                <Link to="/admin-next/accounts">
                  <Button
                    variant="primary"
                    size="md"
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Open Accounts
                  </Button>
                </Link>
              </div>
            </div>

            {/* Candidate / Legislator Management */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Candidate / Legislator Management</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Manage candidates and legislators: TRACER import, profiles, and photos
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Import from TRACER Candidate Search</li>
                    <li>• Link campaign finance committees</li>
                    <li>• Manage candidate profiles and photos</li>
                  </ul>
                </div>
                <Link to="/admin-next/people">
                  <Button
                    variant="primary"
                    size="md"
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Manage candidates and legislators
                  </Button>
                </Link>
              </div>
            </div>

            {/* Election Cycles */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Election Cycles</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Calendars, contests, and ballot initiatives per cycle
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Calendars: filing, primary, and general dates</li>
                    <li>• Contests: races per cycle (expected / official)</li>
                    <li>• Ballot initiatives: measures and propositions</li>
                    <li>• Shared across all races in a jurisdiction</li>
                  </ul>
                </div>
                <Link to="/admin/election-calendars">
                  <Button
                    variant="primary"
                    size="md"
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Manage Election Cycles
                  </Button>
                </Link>
              </div>
            </div>

            {/* Offices */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Vote className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Offices</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Global office catalog shared by all election cycles
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Add offices (e.g. CU Regent, State Board of Ed)</li>
                    <li>• Set ballot order, level, and vote-for</li>
                    <li>• By-district offices reference a district type</li>
                    <li>• Contests in each cycle build on these</li>
                  </ul>
                </div>
                <Link to="/admin/offices">
                  <Button
                    variant="primary"
                    size="md"
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Manage Offices
                  </Button>
                </Link>
              </div>
            </div>

            {/* Data Import */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Data Import</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Import legislative data from various sources
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Quick Colorado bill download</li>
                    <li>• Batch import authentic bills</li>
                    <li>• Enhance existing data</li>
                    <li>• Monitor import progress</li>
                    <li>• District map PDFs (District maps tab)</li>
                  </ul>
                </div>
                <Link to="/admin/data-import">
                  <Button 
                    variant="primary" 
                    size="md" 
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Import Data
                  </Button>
                </Link>
              </div>
            </div>

            {/* Calendar Email Digest */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Calendar Emails</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Send nightly calendar digest emails to subscribers
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• View scraper status</li>
                    <li>• Trigger email jobs</li>
                    <li>• Monitor send progress</li>
                    <li>• View job history</li>
                  </ul>
                </div>
                <Link to="/admin/calendar-emails">
                  <Button 
                    variant="primary" 
                    size="md" 
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Manage Emails
                  </Button>
                </Link>
              </div>
            </div>

            {/* Client Announcements */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Client Announcements</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Post in-app notifications to targeted client groups
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Notify calendar digest subscribers</li>
                    <li>• Target saved client groups</li>
                    <li>• Preview recipient counts</li>
                    <li>• View send history</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <Link to="/admin/announcements">
                    <Button
                      variant="primary"
                      size="md"
                      icon={ArrowRight}
                      iconPosition="right"
                      fullWidth
                    >
                      Send Announcement
                    </Button>
                  </Link>
                  <Link to="/admin/audience-groups">
                    <Button
                      variant="secondary"
                      size="md"
                      icon={PeopleIcon}
                      fullWidth
                    >
                      Manage Groups
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Email Templates Management */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Email Templates</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Manage outbound email templates stored in S3
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Create and edit email templates</li>
                    <li>• Preview emails with sample data</li>
                    <li>• Manage base layout and partials</li>
                    <li>• Version history and rollback</li>
                  </ul>
                </div>
                <Link to="/admin/email-templates">
                  <Button 
                    variant="primary" 
                    size="md" 
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Manage Templates
                  </Button>
                </Link>
              </div>
            </div>

            {/* Outreach Templates Management */}
            <div className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="h-5 w-5 text-primary-700" />
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Outreach Templates</h3>
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                  Manage system-wide outreach, survey, and campaign templates
                </p>
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-4">
                  <ul className="space-y-1">
                    <li>• Create campaign templates</li>
                    <li>• Build survey questionnaires</li>
                    <li>• Configure demographics collection</li>
                    <li>• Manage poll templates</li>
                  </ul>
                </div>
                <Link to="/admin/outreach-templates">
                  <Button 
                    variant="primary" 
                    size="md" 
                    icon={ArrowRight}
                    iconPosition="right"
                    fullWidth
                  >
                    Manage Templates
                  </Button>
                </Link>
              </div>
            </div>

          </div>
    </div>
  );
};

export default AdminPage; 